// Tenantra Inc. - Admin: landlord rent wallet applications (Zum partner customers)
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Pill } from '@/components/dashboard/ScreeningAddresses';
import { DetailSheet, Section, Field, fmtDate } from '@/components/dashboard/DetailPanel';

const ST = { submitted: ['Submitted', 'amber'], under_review: ['Under review', 'amber'], active: ['Active', 'emerald'], rejected: ['Rejected', 'red'] };
const FILES = [['signer_id', 'Signer ID'], ['registration', 'Business registration'], ['void_cheque', 'Void cheque']];

const openFile = async (id) => {
  try {
    const r = await api.get(`/files/${id}`, { responseType: 'blob' });
    window.open(URL.createObjectURL(r.data), '_blank');
  } catch (e) { toast.error(apiHelpers.handleError(e)); }
};

function WalletSheet({ w, onClose, onSaved }) {
  const [status, setStatus] = useState(w?.status);
  const [walletId, setWalletId] = useState(w?.zum_wallet_id || '');
  const [note, setNote] = useState(w?.note || '');
  const [busy, setBusy] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [savingLogin, setSavingLogin] = useState(false);
  useEffect(() => { setStatus(w?.status); setWalletId(w?.zum_wallet_id || ''); setNote(w?.note || ''); setShowLogin(false); setUsername(''); setPassword(''); }, [w]);
  const saveLogin = async () => {
    if (!username.trim() || !password) { toast.error('Enter the Zum username and password'); return; }
    setSavingLogin(true);
    try {
      const r = await api.patch(`/admin/wallets/${w.landlord_id}/login`, { zum_username: username.trim(), zum_password: password });
      toast.success('Login saved ✓');
      if (r.data?.fee_user_warning) toast.message('Login saved, but the fee user could not be created yet', { description: r.data.fee_user_warning });
      setPassword(''); onSaved(); onClose();
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setSavingLogin(false); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/wallets/${w.landlord_id}`, { status, zum_wallet_id: walletId || null, note: note || null }); toast.success('Saved'); onSaved(); onClose(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  return (
    <DetailSheet open={!!w} onClose={onClose} crumb="Rent wallets" title={w?.legal_name} testId="admin-wallet-sheet"
      meta={w && <Pill tone={(ST[w.status] || [])[1]}>{(ST[w.status] || [w.status])[0]}</Pill>}
      footer={<button onClick={save} disabled={busy} className="h-9 px-4 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium inline-flex items-center gap-2" data-testid="admin-wallet-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save</button>}>
      {w && <>
        <Section title="Business">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Landlord">{w.landlord_name}</Field><Field label="Type">{{ individual: 'Individual landlord', corporation: 'Corporation', partnership: 'Partnership', non_profit: 'Non-profit' }[w.entity_type] || w.entity_type}</Field>
            <Field label="Business number">{w.business_number || '–'}</Field><Field label="Plan">{w.plan} · {w.plan_status || '–'}</Field>
            <Field label="Address" wide>{w.address ? `${w.address.street}, ${w.address.city}, ${w.address.province} ${w.address.postal_code}` : '–'}</Field>
            <Field label="Zum customer ID" wide><span className="font-mono text-xs">{w.zum_customer_id || '–'}</span></Field>
          </dl>
        </Section>
        <Section title="Owners 25%+">{(w.owners || []).length ? <ul className="space-y-1 text-sm">{w.owners.map((o, i) => <li key={i}>{o.name} · {o.pct}% · {o.dob}</li>)}</ul> : <p className="text-sm text-slate-400">–</p>}</Section>
        <Section title="Signer"><dl className="grid grid-cols-2 gap-4"><Field label="Name">{w.signer?.name}</Field><Field label="Title">{w.signer?.title}</Field><Field label="Didit">{w.signer?.didit_status || '–'}</Field></dl></Section>
        <Section title="Payout"><p className="text-sm">{w.payout_institution}-{w.payout_transit} ••••{w.payout_last4}</p></Section>
        <Section title="Zum login">
          {w.has_login && !showLogin ? (
            <div className="flex items-center justify-between gap-3" data-testid="admin-wallet-login-saved">
              <div>
                <p className="text-sm font-medium text-emerald-700">Login saved ✓</p>
                <p className="text-xs text-slate-500">Collections, fee transfers and payouts use this login.</p>
              </div>
              <button type="button" onClick={() => setShowLogin(true)} className="h-8 px-3 rounded-md border border-slate-200 text-xs text-teal-700 hover:bg-slate-50" data-testid="admin-wallet-replace-login">Replace login</button>
            </div>
          ) : (
            <div className="space-y-2.5">
              <p className="text-xs text-slate-500">Enter this landlord's own Zum API login. On save we verify it, find the customer & wallet ID, create the fee user and activate the wallet. The password is stored encrypted.</p>
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Zum API username" autoComplete="off" className="h-9 text-sm" data-testid="admin-wallet-zum-username" />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Zum API password" autoComplete="new-password" className="h-9 text-sm" data-testid="admin-wallet-zum-password" />
              <div className="flex items-center gap-2">
                <button type="button" onClick={saveLogin} disabled={savingLogin} className="h-9 px-4 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium inline-flex items-center gap-2" data-testid="admin-wallet-save-login">{savingLogin && <Loader2 className="w-4 h-4 animate-spin" />}Save login</button>
                {w.has_login && <button type="button" onClick={() => { setShowLogin(false); setUsername(''); setPassword(''); }} className="h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>}
              </div>
            </div>
          )}
        </Section>
        <Section title="Documents">
          <div className="flex flex-wrap gap-2">{FILES.filter(([k]) => w.files?.[k]).map(([k, l]) => <button key={k} onClick={() => openFile(w.files[k])} className="h-8 px-3 rounded-md border border-slate-200 text-xs text-teal-700 hover:bg-slate-50" data-testid={`admin-wallet-file-${k}`}>{l}</button>)}</div>
        </Section>
        <Section title="Zum status">
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-1.5">{Object.entries(ST).map(([k, [l]]) => <button key={k} type="button" onClick={() => setStatus(k)} className={`h-8 rounded-md border text-xs ${status === k ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`} data-testid={`admin-wallet-status-${k}`}>{l}</button>)}</div>
            <Input value={walletId} onChange={(e) => setWalletId(e.target.value)} placeholder="Zum wallet ID" className="h-9 font-mono text-xs" data-testid="admin-wallet-id" />
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to landlord" className="h-9 text-sm" data-testid="admin-wallet-note" />
          </div>
        </Section>
      </>}
    </DetailSheet>
  );
}

export function WalletsPanel() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get('/admin/wallets').then(r => setItems(r.data.items)).catch(e => toast.error(apiHelpers.handleError(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="admin-wallets">
      <h1 className="text-[15px] font-medium text-slate-800">Rent wallets</h1>
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-2.5 bg-slate-50/60 border-b border-slate-100 text-xs text-slate-500">
          <div className="col-span-4">Business</div><div className="col-span-2">Plan</div><div className="col-span-2">Submitted</div><div className="col-span-2">Payout</div><div className="col-span-2">Status</div>
        </div>
        {!items ? <div className="p-5"><div className="h-10 bg-slate-100 rounded animate-pulse" /></div> : items.length === 0 ? <p className="px-5 py-8 text-sm text-slate-500 text-center" data-testid="admin-wallets-empty">No applications</p> : (
          <div className="divide-y divide-slate-100">{items.map(w => (
            <div key={w.landlord_id} onClick={() => setOpen(w)} className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-slate-50/80 cursor-pointer transition-colors" data-testid={`admin-wallet-row-${w.landlord_id}`}>
              <div className="col-span-8 sm:col-span-4 min-w-0"><p className="text-sm text-teal-700 truncate">{w.legal_name}</p><p className="text-xs text-slate-500 truncate">{w.landlord_name}</p></div>
              <div className="hidden sm:block col-span-2 text-sm text-slate-700 capitalize">{w.plan}</div>
              <div className="hidden sm:block col-span-2 text-sm text-slate-600">{fmtDate(w.submitted_at)}</div>
              <div className="hidden sm:block col-span-2 text-sm text-slate-600">••••{w.payout_last4}</div>
              <div className="col-span-4 sm:col-span-2"><Pill tone={(ST[w.status] || [])[1]}>{(ST[w.status] || [w.status])[0]}</Pill></div>
            </div>
          ))}</div>
        )}
      </div>
      <WalletSheet w={open} onClose={() => setOpen(null)} onSaved={load} />
    </div>
  );
}
